import React, { useState, useMemo } from "react";
import { useQuery, useMutation, gql } from "@apollo/client";
import { Modal, ModalHeader, ModalBody, ModalFooter, Button, Alert, Progress } from "reactstrap";
import Select from "react-select";
import CopyToClipboard from "../../Material/CopyToClipboard";
import "./IncidentDocumentLink.css";
import * as yup from "yup";

const DOCUMENT_TYPES_QUERY = gql`
  query AllIncidentDocumentTypes($category: String!) {
    allIncidentDocumentTypes(category: $category) {
      edges {
        node {
          id
          name
        }
      }
    }
  }
`;

const SEND_INCIDENT_DOCUMENT_LINK_MUTATION = gql`
  mutation SendIncidentDocumentLink($input: SendIncidentDocumentLinkMutationInput!) {
    sendIncidentDocumentLink(input: $input) {
      ok
      url
      errors {
        messages
      }
    }
  }
`;

const INCIDENT_ALL_CONTACTS_QUERY = gql`
  query IncidentAllContacts($incidentId: ID!) {
    incident(incidentId: $incidentId) {
      id
      documentSlug
      incidentpartySet {
        edges {
          node {
            id
            partyName
            documentSlug
            incidentpartypeopleSet {
              edges {
                node {
                  id
                  email
                  phoneNumber
                }
              }
            }
          }
        }
      }
      rental {
        id
        driver {
          customerhandleSet(includeDeleted: false) {
            edges {
              node {
                id
                type
                handle
              }
            }
          }
        }
      }
    }
  }
`;

const emailSchema = yup
    .string()
    .matches(/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/, "Invalid email format");
const phoneSchema = yup.string().matches(/^\+1[0-9]{10}$/, "Invalid phone number");

const IncidentDocumentLink = ({ isOpen, toggle, category, incidentId, slug }) => {
    const [selectedDocs, setSelectedDocs] = useState([]);
    const [selectedPartyId, setSelectedPartyId] = useState("");
    const [sendMode, setSendMode] = useState(null);
    const [alert, setAlert] = useState(null);
    const [generatedLink, setGeneratedLink] = useState("");
    const [selectedEmails, setSelectedEmails] = useState([]);
    const [selectedPhones, setSelectedPhones] = useState([]);
    const [customEmail, setCustomEmail] = useState("");
    const [customPhone, setCustomPhone] = useState("");
    const [customEmailError, setCustomEmailError] = useState("");
    const [customPhoneError, setCustomPhoneError] = useState("");

    const { data, loading, error } = useQuery(DOCUMENT_TYPES_QUERY, {
        variables: { category },
        skip: !isOpen
    });

    const { data: contactsData } = useQuery(INCIDENT_ALL_CONTACTS_QUERY, {
        variables: { incidentId },
        skip: !incidentId || !isOpen
    });

    const [sendLink, { loading: sending }] = useMutation(SEND_INCIDENT_DOCUMENT_LINK_MUTATION);

    const docTypes = data?.allIncidentDocumentTypes?.edges?.map(e => e.node) || [];
    const partyOptions = contactsData?.incident?.incidentpartySet?.edges?.map(({ node }) => ({
        value: node.id,
        label: node.partyName || "Unknown Party",
        documentSlug: node.documentSlug || ""
    })) || [];
    const selectedPartyOption = partyOptions.find((party) => party.value === selectedPartyId) || null;
    const selectedParty = selectedPartyOption || null;
    const isPartyFlow = category === "PARTY";
    const resolvedSlug = isPartyFlow
        ? (selectedParty?.documentSlug || "")
        : (slug || contactsData?.incident?.documentSlug || "");
    const canSelectDocuments = !isPartyFlow || Boolean(selectedPartyId);
    const { emails, phones } = useMemo(() => {
        const emails = [];
        const phones = [];

        if (isPartyFlow) {
            const selectedPartyNode = contactsData?.incident?.incidentpartySet?.edges?.find(({ node }) => node.id === selectedPartyId)?.node;
            selectedPartyNode?.incidentpartypeopleSet?.edges?.forEach(({ node: person }) => {
                if (person.email) emails.push(person.email);
                if (person.phoneNumber) phones.push(person.phoneNumber);
            });
        } else {
            contactsData?.incident?.rental?.driver?.customerhandleSet?.edges?.forEach(({ node }) => {
                if (node.type === "EMAIL" && node.handle) emails.push(node.handle);
                if (node.type === "PHONE" && node.handle) phones.push(node.handle);
            });

            contactsData?.incident?.incidentpartySet?.edges?.forEach(({ node: party }) => {
                party?.incidentpartypeopleSet?.edges?.forEach(({ node: person }) => {
                    if (person.email) emails.push(person.email);
                    if (person.phoneNumber) phones.push(person.phoneNumber);
                });
            });
        }

        return {
            emails: [...new Set(emails)],
            phones: [...new Set(phones)]
        };
    }, [contactsData, isPartyFlow, selectedPartyId]);

    const toggleDoc = id =>
        setSelectedDocs(prev => (prev.includes(id) ? prev.filter(d => d !== id) : [...prev, id]));

    const toggleValue = (value, setter) =>
        setter(prev => (prev.includes(value) ? prev.filter(v => v !== value) : [...prev, value]));

    const addCustomEmail = async () => {
        setCustomEmailError("");
        if (customEmail && !selectedEmails.includes(customEmail)) {
            try {
                await emailSchema.validate(customEmail);
                setSelectedEmails([...selectedEmails, customEmail]);
                setCustomEmail("");
            } catch (err) {
                setCustomEmailError(err.message);
            }
        }
        else { setCustomEmailError("Contact already added"); }
    };

    const addCustomPhone = async () => {
        setCustomPhoneError("");
        if (customPhone && !selectedPhones.includes(customPhone)) {
            try {
                await phoneSchema.validate(customPhone);
                setSelectedPhones([...selectedPhones, customPhone]);
                setCustomPhone("");
            } catch (err) {
                setCustomPhoneError(err.message);
            }
        }
        else { setCustomPhoneError("Contact already added"); }
    };

    const resetRecipients = () => {
        setSelectedEmails([]);
        setSelectedPhones([]);
        setCustomEmail("");
        setCustomPhone("");
        setCustomEmailError("");
        setCustomPhoneError("");
    };

    const handleSubmit = async () => {
        try {
            setAlert(null);
            if (isPartyFlow && !selectedPartyId) {
                setAlert({ color: "danger", msg: "Please select a party first." });
                return;
            }
            if (!resolvedSlug) {
                setAlert({ color: "danger", msg: "Unable to resolve document destination." });
                return;
            }

            const input = {
                incidentId,
                documentTypes: selectedDocs.map(typeId => ({ typeId, slug: resolvedSlug })),
                contactList: sendMode === "email" ? selectedEmails : sendMode === "sms" ? selectedPhones : null
            };

            const res = await sendLink({ variables: { input } });

            if (res.data?.sendIncidentDocumentLink?.ok) {
                setGeneratedLink(res.data.sendIncidentDocumentLink.url || "");
                setAlert({
                    color: "success",
                    msg: sendMode ? "Link sent successfully" : "Link generated successfully"
                });
                setSendMode(null);
            }

            if (res.data?.sendIncidentDocumentLink?.errors) {
                setAlert({
                    color: "danger",
                    msg: res.data.sendIncidentDocumentLink.errors[0].messages.join(", ")
                });
            }
        } catch {
            setAlert({ color: "danger", msg: "Operation failed" });
        }
    };

    const canGenerate = canSelectDocuments && selectedDocs.length > 0 && !sendMode;
    const canSend = canSelectDocuments && selectedDocs.length > 0 && ((sendMode === "email" && selectedEmails.length > 0) || (sendMode === "sms" && selectedPhones.length > 0));

    return (
        <Modal isOpen={isOpen} toggle={toggle} centered className="doc-modal">
            <ModalHeader className="doc-header">Request Missing Documents</ModalHeader>

            <ModalBody>
                {loading && <Progress animated value={100} />}
                {error && <Alert color="danger">Failed to load documents</Alert>}
                {alert && <Alert color={alert.color}>{alert.msg}</Alert>}

                {generatedLink ? (
                    <>
                        <p className="doc-label mt-3">Link</p>
                        <div className="doc-link-row">
                            <input value={generatedLink} readOnly />
                            <CopyToClipboard textToCopy={generatedLink} displayText="Url" />
                        </div>
                    </>
                ) : (
                    <>
                        {isPartyFlow && (
                            <>
                                <p className="doc-label">Party</p>
                                <Select
                                    options={partyOptions}
                                    value={selectedPartyOption}
                                    className="bos-custom-select"
                                    classNamePrefix="bos-select"
                                    onChange={(option) => {
                                        setSelectedPartyId(option?.value || "");
                                        setSelectedDocs([]);
                                        setSendMode(null);
                                        resetRecipients();
                                    }}
                                    isDisabled={sending}
                                    placeholder="Select party..."
                                />
                            </>
                        )}
                        <p className="doc-label mt-2">Documents</p>
                        <div className={`doc-picker ${!canSelectDocuments ? "is-disabled" : ""}`}>
                            {docTypes.map(d => (
                                <div
                                    key={d.id}
                                    className={`doc-option ${selectedDocs.includes(d.id) ? "selected" : ""}`}
                                    onClick={() => canSelectDocuments && toggleDoc(d.id)}
                                >
                                    {d.name}
                                </div>
                            ))}
                        </div>

                        {selectedDocs.length > 0 && (
                            <div className="doc-chips-container mb-2">
                                {selectedDocs.map(id => {
                                    const doc = docTypes.find(d => d.id === id);
                                    return (
                                        <span key={id} className="doc-chip">
                                            {doc?.name || id}
                                            <i
                                                className="fa fa-times ml-1"
                                                onClick={() => toggleDoc(id)}
                                                style={{ cursor: "pointer" }}
                                            />
                                        </span>
                                    );
                                })}
                            </div>
                        )}

                        <p className="doc-label mt-3">Send Via</p>
                        <div className="doc-actions">
                            <button
                                className={`pill-btn ${sendMode === "email" ? "selected" : ""}`}
                                disabled={!canSelectDocuments || !selectedDocs.length}
                                onClick={() => {
                                    if (sendMode === "email") {
                                        setSendMode(null);
                                        resetRecipients();
                                    } else {
                                        resetRecipients();
                                        setSendMode("email");
                                    }
                                }}
                            >
                                <i className="fa fa-envelope pill-icon" />&nbsp;Email
                            </button>

                            <button
                                className={`pill-btn ${sendMode === "sms" ? "selected" : ""}`}
                                disabled={!canSelectDocuments || !selectedDocs.length}
                                onClick={() => {
                                    if (sendMode === "sms") {
                                        setSendMode(null);
                                        resetRecipients();
                                    } else {
                                        resetRecipients();
                                        setSendMode("sms");
                                    }
                                }}
                            >
                                <i className="fa fa-commenting pill-icon" />&nbsp;SMS
                            </button>
                        </div>

                        {sendMode === "email" && (
                            <>
                                <p className="doc-label mt-3">Select Email(s)</p>
                                <div className="doc-picker">
                                    {emails.map(e => (
                                        <div
                                            key={e}
                                            className={`doc-option ${selectedEmails.includes(e) ? "selected" : ""}`}
                                            onClick={() => toggleValue(e, setSelectedEmails)}
                                        >
                                            {e}
                                        </div>
                                    ))}
                                </div>

                                <div className="doc-chips-container">
                                    {selectedEmails.map(e => (
                                        <span key={e} className="doc-chip">
                                            {e}
                                            <i
                                                className="fa fa-times ml-1"
                                                onClick={() => setSelectedEmails(selectedEmails.filter(x => x !== e))}
                                            />
                                        </span>
                                    ))}
                                </div>

                                <div className="d-flex mt-2">
                                    <input
                                        type="email"
                                        className={`form-control${customEmailError ? " is-invalid" : ""}`}
                                        placeholder="Add another email"
                                        value={customEmail}
                                        onChange={e => setCustomEmail(e.target.value)}
                                        onKeyDown={e => e.key === "Enter" && addCustomEmail()}
                                    />
                                    <Button color="secondary" className="ml-2" onClick={addCustomEmail} disabled={!customEmail}>
                                        Add
                                    </Button>
                                </div>
                                {customEmailError && <p className="invalid-feedback d-block mb-0">{customEmailError}</p>}
                            </>
                        )}

                        {sendMode === "sms" && (
                            <>
                                <p className="doc-label mt-3">Select Phone(s)</p>
                                <div className="doc-picker">
                                    {phones.map(p => (
                                        <div
                                            key={p}
                                            className={`doc-option ${selectedPhones.includes(p) ? "selected" : ""}`}
                                            onClick={() => toggleValue(p, setSelectedPhones)}
                                        >
                                            {p}
                                        </div>
                                    ))}
                                </div>

                                <div className="doc-chips-container">
                                    {selectedPhones.map(p => (
                                        <span key={p} className="doc-chip">
                                            {p}
                                            <i
                                                className="fa fa-times ml-1"
                                                onClick={() => setSelectedPhones(selectedPhones.filter(x => x !== p))}
                                            />
                                        </span>
                                    ))}
                                </div>

                                <div className="d-flex mt-2">
                                    <input
                                        type="tel"
                                        className={`form-control${customPhoneError ? " is-invalid" : ""}`}
                                        placeholder="Add another phone"
                                        value={customPhone}
                                        onChange={e => setCustomPhone(e.target.value)}
                                        onKeyDown={e => e.key === "Enter" && addCustomPhone()}
                                    />
                                    <Button color="secondary" className="ml-2" onClick={addCustomPhone} disabled={!customPhone}>
                                        Add
                                    </Button>
                                </div>
                                {customPhoneError && <p className="invalid-feedback d-block mb-0">{customPhoneError}</p>}
                            </>
                        )}
                    </>
                )}
            </ModalBody>

            <ModalFooter className="doc-footer">
                {!generatedLink && canGenerate && (
                    <Button color="success" disabled={sending} onClick={handleSubmit}>
                        Generate Link
                    </Button>
                )}

                {!generatedLink && sendMode && (
                    <Button color="success" disabled={sending || !canSend} onClick={handleSubmit}>
                        Send
                    </Button>
                )}

                <Button color="primary" onClick={toggle}>
                    Close
                </Button>
            </ModalFooter>
        </Modal>
    );
};

export default IncidentDocumentLink;
